import { useLabels } from "@/core/context/LabelsContext"
import ProductCard from "@/product/card/ProductCard"
import MemberCurriculumProgressBar from "@/product/reports/page/curriculum/MemberCurriculumProgressBar"
import useIsWebView from "@/product/util/hook/useIsWebView"
import { GlobalID } from "@/relay/RelayTypes"
import useTrackUserActivity from "@/reporting/useTrackUserActivity"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoText } from "@disco-ui"
import { useTheme } from "@material-ui/core"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import pluralize from "pluralize"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"
import { ContinueYourProductCardFragment$key } from "./__generated__/ContinueYourProductCardFragment.graphql"

type ContinueYourProductCardVariant = "wide" | "tall"

interface Props {
  blockId: GlobalID
  productKey: ContinueYourProductCardFragment$key
  variant: ContinueYourProductCardVariant
}

export default function ContinueYourProductCard({ blockId, productKey, variant }: Props) {
  const labels = useLabels()
  const theme = useTheme()
  const trackUserActivity = useTrackUserActivity()
  const isMobileView = useIsMobile()
  const isWebView = useIsWebView()
  const isMobile = isMobileView || isWebView

  const product = useFragment<ContinueYourProductCardFragment$key>(
    graphql`
      fragment ContinueYourProductCardFragment on Product {
        ...ProductCardFragment
        todos
        curriculum {
          id
        }
        viewerMembership {
          ...MemberCurriculumProgressBarFragment
        }
      }
    `,
    productKey
  )

  const finalVariant = isMobile ? "tall" : variant

  const classes = useStyles({ hasTodos: product.todos > 0, variant: finalVariant })

  return (
    <ProductCard
      productKey={product}
      hideAdminActions
      addShadow
      className={classes.card}
      titleVariant={"body-lg-600"}
      onClick={handleTrackUserActivity}
      linkLocation={product.curriculum ? "curriculum" : "dashboard"}
      coverPosition={finalVariant === "wide" ? "left" : "top"}
      isDashboardBlock
      tagType={"label"}
      hideButton
      footer={
        <div className={classes.footer}>
          <span>
            {product.todos > 0 && (
              <div className={classes.tag}>
                <DiscoIcon
                  icon={"iconsax.lamp-on"}
                  color={theme.palette.groovy.neutral[700]}
                  height={16}
                  width={16}
                />
                <DiscoText
                  testid={"ContinueYourProductsDashboardBlock.card-footer"}
                  variant={"body-xs-600"}
                >{`${product.todos} ${pluralize(
                  labels.assignment.singular,
                  product.todos
                )} or ${pluralize("Quiz", product.todos)}`}</DiscoText>
              </div>
            )}
          </span>
          {product.viewerMembership && product.curriculum && (
            <MemberCurriculumProgressBar
              memberKey={product.viewerMembership}
              width={"100px"}
            />
          )}
        </div>
      }
    />
  )

  function handleTrackUserActivity() {
    trackUserActivity({
      kind: "clicked",
      entityId: blockId,
    })
  }
}

interface StyleProps {
  hasTodos: boolean
  variant: ContinueYourProductCardVariant
}

const useStyles = makeUseStyles((theme) => ({
  card: ({ variant }: StyleProps) => ({
    minHeight: variant === "wide" ? "130px" : "292px",
    height: variant === "wide" ? "130px" : "292px",
  }),
  footer: ({ variant }: StyleProps) => ({
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    ...(variant === "wide"
      ? {
          alignItems: "center",
          justifyContent: "space-between",
        }
      : {
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column-reverse",
        }),
  }),
  tag: {
    backgroundColor: theme.palette.groovy.neutral[200],
    borderRadius: theme.measure.borderRadius.xl,
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    width: "fit-content",
  },
}))
